import { useEffect, useState } from "react";
import { api, formatCOP, formatNumber } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Boxes, AlertTriangle, ArrowRight } from "lucide-react";

/**
 * Re-expresa los lotes de uno o varios productos en la unidad de venta:
 * cantidad ×N y costo ÷N (el valor del inventario no cambia).
 * - `productoId`  → modo individual (desde la ficha / lotes)
 * - `productoIds` → modo masivo (desde la barra de selección)
 */
export default function DesglosarEmpaqueDialog({ open, onClose, onDone, productoId, productoIds, productoNombre }) {
  const masivo = Array.isArray(productoIds) && productoIds.length > 0;
  const [factor, setFactor] = useState("");
  const [precios, setPrecios] = useState("no");
  const [pctMin, setPctMin] = useState("");
  const [pctSug, setPctSug] = useState("");
  const [precioMin, setPrecioMin] = useState("");
  const [precioSug, setPrecioSug] = useState("");
  const [guardar, setGuardar] = useState(true);
  const [ventasPrevias, setVentasPrevias] = useState("sub_paquete");
  const [motivo, setMotivo] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const idsKey = masivo ? productoIds.join(",") : productoId;
  useEffect(() => {
    if (!open) return;
    setFactor(""); setPrecios("no"); setPctMin(""); setPctSug(""); setPrecioMin(""); setPrecioSug(""); setGuardar(true); setVentasPrevias("sub_paquete"); setMotivo(""); setPreview(null);
  }, [open, idsKey]);

  const body = () => ({
    factor: Number(factor), motivo: motivo.trim() || null, ajustar_precios: precios, guardar_factor_empaque: guardar, ventas_previas: ventasPrevias,
    pct_margen_minimo: precios === "margenes" ? Number(pctMin) : null,
    pct_margen_sugerido: precios === "margenes" ? Number(pctSug) : null,
    precio_minimo_manual: precios === "manual" ? Math.round(Number(precioMin)) : null,
    precio_sugerido_manual: precios === "manual" ? Math.round(Number(precioSug)) : null,
    ...(masivo ? { producto_ids: productoIds } : {}),
  });
  const url = (suffix) => masivo ? `/productos/_/desglosar-masivo${suffix}` : `/productos/${productoId}/desglosar${suffix}`;

  const calcular = async () => {
    if (!Number(factor) || Number(factor) <= 1) return toast.error("El factor debe ser mayor que 1 (ej. 20 sub-paquetes por bolsa, o 12.5 metros por paquete)");
    if (precios === "margenes" && (pctMin === "" || pctSug === "")) return toast.error("Indica los % de margen mínimo y sugerido");
    if (precios === "manual" && (precioMin === "" || precioSug === "")) return toast.error("Escribe el precio mínimo y el sugerido");
    setLoading(true);
    try {
      const { data } = await api.post(url("/preview"), body());
      setPreview(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo calcular"); }
    finally { setLoading(false); }
  };

  const aplicar = async () => {
    if (motivo.trim().length < 5) return toast.error("Escribe el motivo (mínimo 5 caracteres)");
    setSaving(true);
    try {
      const { data } = await api.post(url(""), body());
      if (masivo) toast.success(`Desglose ×${data.factor} aplicado a ${data.ok} producto(s)${data.con_error ? ` · ${data.con_error} con error` : ""}`);
      else toast.success(`Desglose ×${data.factor}: stock ${formatNumber(data.stock_viejo)} → ${formatNumber(data.stock_nuevo)} · ${data.lotes} lote(s)`);
      onDone?.(data); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo aplicar"); }
    finally { setSaving(false); }
  };

  const resultados = preview ? (masivo ? preview.resultados : [preview]) : [];
  const errores = preview?.errores || [];

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !saving && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="desglosar-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Boxes className="w-5 h-5 text-primary" /> Desglosar empaque {masivo ? `· ${productoIds.length} productos` : productoNombre ? `· ${productoNombre}` : ""}
          </DialogTitle>
          <p className="text-xs text-muted-foreground mt-1">
            El proveedor factura por <b>paquete maestro</b> (ej. bolsa) pero tú vendes el <b>sub-paquete</b>. Escribe cuántas unidades de venta trae cada paquete:
            el sistema multiplica las cantidades de todos los lotes y divide el costo. <b>El valor del inventario no cambia.</b>
          </p>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3 mt-2">
          <div>
            <Label>Unidades de venta por paquete (×N)</Label>
            <Input type="number" min="1.01" step="0.01" value={factor} onChange={(e) => { setFactor(e.target.value); setPreview(null); }} className="h-11 mt-1" placeholder="Ej: 20 o 12.5" data-testid="desglosar-factor" />
          </div>
          <div>
            <Label>Precios de venta (mín / sugerido)</Label>
            <select value={precios} onChange={(e) => { setPrecios(e.target.value); setPreview(null); }} className="h-11 mt-1 w-full rounded-md border border-input bg-background px-3 text-sm" data-testid="desglosar-precios">
              <option value="no">No tocar precios (ya vendo por unidad a estos precios)</option>
              <option value="dividir">Dividir ÷N (mis precios estaban por paquete)</option>
              <option value="margenes">Recalcular con % de margen sobre el costo nuevo</option>
              {!masivo && <option value="manual">Escribir yo los precios (mín / sugerido)</option>}
            </select>
          </div>
          {precios === "manual" && (
            <>
              <div>
                <Label className="text-xs">Precio mínimo (COP por unidad)</Label>
                <Input type="number" min="0" value={precioMin} onChange={(e) => { setPrecioMin(e.target.value); setPreview(null); }} className="h-10 mt-1" placeholder="Ej: 1200" data-testid="desglosar-precio-min" />
              </div>
              <div>
                <Label className="text-xs">Precio sugerido (COP por unidad)</Label>
                <Input type="number" min="0" value={precioSug} onChange={(e) => { setPrecioSug(e.target.value); setPreview(null); }} className="h-10 mt-1" placeholder="Ej: 1500" data-testid="desglosar-precio-sug" />
              </div>
            </>
          )}
          {precios === "margenes" && (
            <>
              <div>
                <Label className="text-xs">% Margen mínimo</Label>
                <Input type="number" value={pctMin} onChange={(e) => { setPctMin(e.target.value); setPreview(null); }} className="h-10 mt-1" data-testid="desglosar-pct-min" />
              </div>
              <div>
                <Label className="text-xs">% Margen sugerido</Label>
                <Input type="number" value={pctSug} onChange={(e) => { setPctSug(e.target.value); setPreview(null); }} className="h-10 mt-1" data-testid="desglosar-pct-sug" />
              </div>
            </>
          )}
        </div>
        <label className="mt-3 flex items-start gap-2 cursor-pointer rounded-lg border border-border p-3" data-testid="desglosar-guardar-toggle">
          <input type="checkbox" checked={guardar} onChange={(e) => setGuardar(e.target.checked)} className="mt-0.5 accent-primary" data-testid="desglosar-guardar-check" />
          <div>
            <div className="text-sm font-semibold">Recordar el factor de empaque para próximos contenedores</div>
            <div className="text-[11px] text-muted-foreground">Cuando este producto vuelva a llegar en un Excel, el importador convertirá automáticamente cantidad ×N y costo ÷N.</div>
          </div>
        </label>
        <div className="mt-3 rounded-lg border border-border p-3">
          <div className="text-sm font-semibold mb-1">¿Cómo se vendía este producto ANTES del desglose?</div>
          <div className="grid sm:grid-cols-2 gap-2">
            <label className={`flex items-start gap-2 cursor-pointer rounded-md border p-2 ${ventasPrevias === "sub_paquete" ? "border-primary bg-primary/5" : "border-border"}`} data-testid="desglosar-ventas-sub">
              <input type="radio" name="ventas_previas" checked={ventasPrevias === "sub_paquete"} onChange={() => { setVentasPrevias("sub_paquete"); setPreview(null); }} className="mt-0.5 accent-primary" />
              <div>
                <div className="text-xs font-semibold">Ya se vendía por sub-paquete (unidad nueva)</div>
                <div className="text-[11px] text-muted-foreground">Cada sub-paquete vendido descontó 1 paquete del lote y se costeó al precio del paquete. Se corrige el costo de esos pedidos (÷N) y se recupera el stock que se descontó de más.</div>
              </div>
            </label>
            <label className={`flex items-start gap-2 cursor-pointer rounded-md border p-2 ${ventasPrevias === "paquete" ? "border-primary bg-primary/5" : "border-border"}`} data-testid="desglosar-ventas-paq">
              <input type="radio" name="ventas_previas" checked={ventasPrevias === "paquete"} onChange={() => { setVentasPrevias("paquete"); setPreview(null); }} className="mt-0.5 accent-primary" />
              <div>
                <div className="text-xs font-semibold">Se vendían paquetes completos</div>
                <div className="text-[11px] text-muted-foreground">Las cantidades de los pedidos anteriores eran paquetes. Solo se re-expresa el costo por lote; el valor de esas ventas no cambia.</div>
              </div>
            </label>
          </div>
        </div>

        {!preview && (
          <Button onClick={calcular} disabled={loading} variant="outline" className="mt-3 h-11" data-testid="desglosar-calcular-btn">
            {loading ? "Calculando..." : "Ver qué cambiaría"}
          </Button>
        )}

        {preview && (
          <div className="mt-3 space-y-3" data-testid="desglosar-preview">
            {errores.length > 0 && (
              <div className="text-[11px] text-rose-700 dark:text-rose-300 bg-rose-50 dark:bg-rose-900/20 rounded-md p-2 space-y-0.5" data-testid="desglosar-errores">
                <div className="font-semibold flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> {errores.length} producto(s) no se pueden desglosar:</div>
                {errores.map((er) => <div key={er.producto_id}>· {er.error}</div>)}
              </div>
            )}
            {resultados.map((r) => (
              <div key={r.producto_id} className="rounded-lg border border-border p-3 space-y-2" data-testid={`desglosar-res-${r.producto_id}`}>
                {masivo && <div className="text-sm font-semibold">{r.nombre}{r.codigo && <span className="text-muted-foreground"> · {r.codigo}</span>}</div>}
                {(r.advertencias || []).length > 0 && (
                  <div className="text-[11px] text-amber-800 dark:text-amber-200 bg-amber-50 dark:bg-amber-900/20 rounded-md p-2 space-y-0.5" data-testid="desglosar-advertencias">
                    {r.advertencias.map((a, i) => <div key={i} className="flex items-start gap-1"><AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" /> {a}</div>)}
                  </div>
                )}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                  <Dato label="Stock" a={formatNumber(r.stock_viejo)} b={formatNumber(r.stock_nuevo)} testid="desglosar-stock" />
                  <Dato label="Costo ficha" a={formatCOP(r.costo_ficha_viejo || 0)} b={formatCOP(r.costo_ficha_nuevo || 0)} />
                  <Dato label="Precio mín" a={formatCOP(r.precio_minimo_viejo || 0)} b={formatCOP(r.precio_minimo_nuevo || 0)} />
                  <Dato label="Precio sug" a={formatCOP(r.precio_sugerido_viejo || 0)} b={formatCOP(r.precio_sugerido_nuevo || 0)} />
                </div>
                <div className="text-[11px] text-muted-foreground">
                  {r.lotes} lote(s) · {r.despachos_a_reexpresar} despacho(s) histórico(s) se re-expresan{r.pedidos_snapshot_a_corregir > 0 && <> · {r.pedidos_snapshot_a_corregir} pedido(s) con costo congelado se corrigen ÷{r.factor}</>} · valor inventario {formatCOP(r.valor_inventario)}{r.valor_inventario_nuevo != null && r.valor_inventario_nuevo !== r.valor_inventario ? <> → <b>{formatCOP(r.valor_inventario_nuevo)}</b> (recupera lo descontado de más)</> : " (no cambia)"}
                  {r.unidades_por_caja_nuevo && <> · und/caja {formatNumber(r.unidades_por_caja_viejo)} → {formatNumber(r.unidades_por_caja_nuevo)}</>}
                </div>
                <div className="max-h-40 overflow-y-auto rounded-md border border-border">
                  <table className="w-full text-[11px]">
                    <thead className="bg-muted/50 sticky top-0"><tr><th className="text-left p-1.5">Lote</th><th className="text-right p-1.5">Recibido</th><th className="text-right p-1.5">Disponible</th><th className="text-right p-1.5">Costo</th></tr></thead>
                    <tbody>
                      {r.filas.map((f) => (
                        <tr key={f.lote_id} className="border-t border-border/60 tabular-nums">
                          <td className="p-1.5">{f.contenedor_id || "—"}</td>
                          <td className="p-1.5 text-right"><span className="text-muted-foreground line-through mr-1">{formatNumber(f.cantidad_recibida)}</span>{formatNumber(f.cantidad_recibida_nueva)}</td>
                          <td className="p-1.5 text-right"><span className="text-muted-foreground line-through mr-1">{formatNumber(f.cantidad_disponible)}</span>{formatNumber(f.cantidad_disponible_nueva)}</td>
                          <td className="p-1.5 text-right"><span className="text-muted-foreground line-through mr-1">{formatCOP(f.costo_viejo)}</span><b>{formatCOP(f.costo_nuevo)}</b></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
            {resultados.length > 0 && (
              <>
                <div>
                  <Label>Motivo *</Label>
                  <Input value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Ej: los moños vienen en bolsa de 20 paquetes; vendemos por paquete" className="h-11 mt-1" data-testid="desglosar-motivo" />
                </div>
                <div className="flex items-start gap-2 text-[11px] text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 rounded-md p-2">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" /> Queda en auditoría y movimientos. {ventasPrevias === "sub_paquete" ? "Los pedidos anteriores conservan sus cantidades (ya eran sub-paquetes); su costo congelado y el consumo de lote se corrigen ÷N." : "Los pedidos ya despachados conservan sus cantidades originales (paquetes); solo se re-expresa el costo consumido por lote."}
                </div>
              </>
            )}
          </div>
        )}

        <DialogFooter className="mt-3">
          <Button variant="ghost" onClick={onClose} disabled={saving} data-testid="desglosar-cancelar-btn">Cancelar</Button>
          {preview && resultados.length > 0 && (
            <Button onClick={aplicar} disabled={saving} className="bg-primary" data-testid="desglosar-aplicar-btn">
              {saving ? "Aplicando..." : `Aplicar desglose ×${factor}`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Dato({ label, a, b, testid }) {
  return (
    <div className="rounded-md border border-border bg-card p-2" data-testid={testid}>
      <div className="text-[9px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="flex items-center gap-1 tabular-nums"><span className="text-muted-foreground line-through">{a}</span><ArrowRight className="w-3 h-3 text-primary" /><b>{b}</b></div>
    </div>
  );
}
